import { excludeKeys, META_PIXEL_BASIC_PROPERTIES } from '@repo/common-utils';
import { useUserEventLogger } from '@repo/tracking-service';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';

import { useSectionScrollModel } from '@/components/common/context/SectionScrollModelProvider';
import { useApplyRefund } from '@/components/tax-refund/common/hooks//use-apply-refund';
import { useTritxApplyRefund } from '@/components/tax-refund/common/hooks//use-tritx-apply-refund';
import { getNaverPixelItems } from '@/components/tax-refund/common/utils/naver-pixel-items';
import { useRefundConversionData } from '@/components/tax-refund/lookup/result/apply-possible/use-apply-possible-refund-data';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { poaSaveResultAtom } from '@/lib/stores/power-of-attorney.ts';

export function useBothApplyRefunds() {
  const router = useRouter();
  const { eventAttribute } = useSectionScrollModel();
  const poaSaveResult = useAtomValue(poaSaveResultAtom);
  const { sendViewEvent, sendCustomActionEvent, sendMetaEvent, sendKakaoEvent, sendNaverEvent } = useUserEventLogger();
  const { applyRefund, isInFlight } = useApplyRefund();
  const { tritxApplyRefund, isInFlight: isInFlightTritx } = useTritxApplyRefund();
  const { getConversionData } = useRefundConversionData();

  const bothApplyRefunds = ({
    variables,
    refundAmountTotal,
    refundAmount,
    businessType
  }: {
    variables: {
      refundId: string;
      phoneNumber: string;
      bank: string;
      accountOwner: string;
      accountNo: string;
    };
    refundAmountTotal: number;
    refundAmount: number;
    businessType: 'indis' | 'corps';
  }) => {
    const isIncomeIncluded = sessionStorage.getItem('includeGlobalIncomeTax') === 'true';
    const isTritxIncluded = sessionStorage.getItem('includeCapitalGainsTax') === 'true';
    const metaProperties = sessionStorage.getItem(META_PIXEL_BASIC_PROPERTIES) ?? undefined;

    const applyRefundPromise = isIncomeIncluded
      ? new Promise<void>((resolve, reject) => {
          applyRefund({
            variables: {
              ...variables,
              ...poaSaveResult,
              metaProperties
            },
            onCompleted: () => {
              sessionStorage.removeItem('includeGlobalIncomeTax');
              resolve();
            },
            onError: () => {
              reject();
            }
          });
        })
      : Promise.resolve();

    const tritxApplyRefundPromise = isTritxIncluded
      ? new Promise<void>((resolve, reject) => {
          tritxApplyRefund({
            variables: {
              ...variables,
              ...poaSaveResult,
              metaProperties
            },
            onCompleted: () => {
              sessionStorage.removeItem('includeCapitalGainsTax');
              resolve();
            },
            onError: () => {
              reject();
            }
          });
        })
      : Promise.resolve();

    Promise.all([applyRefundPromise, tritxApplyRefundPromise])
      .then(() => {
        const isOnlyApplyCapitalGainsTax = !isIncomeIncluded && isTritxIncluded;
        const queryParams = {
          ...router.query,
          ...(isOnlyApplyCapitalGainsTax && { onlyTritx: 'true' }),
          type: businessType
        };
        const { totalAmount, incomeAmount, tritxAmount, refundType, refundGrade, products } = getConversionData(
          businessType,
          isIncomeIncluded ? refundAmount : 0,
          isIncomeIncluded ? 'ApplyPossible' : '',
          isTritxIncluded ? refundAmountTotal - refundAmount : 0,
          isTritxIncluded ? 'ApplyPossible' : ''
        );

        router
          .push({
            pathname: COMMON_PATHS.환급신청완료,
            query: queryParams
          })
          .then(() => {
            sendViewEvent(
              'apply_complete',
              { ...excludeKeys(eventAttribute ?? {}, ['current_section']), businessType, amount: refundAmount ?? 0 },
              { isUseAirbridge: true, isUseGoogleAnalytics: true, isUseTiktokPixel: false }
            );
            sendCustomActionEvent('Purchase', '', { businessType, amount: refundAmountTotal }, { isUseTiktokPixel: true, isUseMixpanel: false });
            sendMetaEvent('Purchase', {
              contents_category: businessType,
              contents_type: 'product',
              contents_name: refundType,
              user_grade: refundGrade,
              value: totalAmount,
              value_income: incomeAmount,
              value_gains: tritxAmount
            });
            sendKakaoEvent('purchase', {
              total_price: totalAmount,
              currency: 'KRW',
              total_quantity: products.length,
              tag: refundType,
              products
            });
            sendNaverEvent({
              id: variables.refundId,
              type: 'purchase',
              value: String(totalAmount),
              currency: 'KRW',
              items: getNaverPixelItems(businessType, incomeAmount, tritxAmount)
            });
          });
      })
      .catch((reason) => {
        console.error('Refund application failed:', reason);
      });
  };

  return {
    bothApplyRefunds,
    isInFlight,
    isInFlightTritx
  };
}
